import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { SchedulePicker, ScheduledList } from "./SchedulePanel";

const sel = "h-10 w-full rounded-md border border-zap-line bg-white px-2 text-sm";

const History = ({ items }) => (
  <section className="space-y-2">
    <h3 className="font-display text-base font-semibold">Envios deste grupo</h3>
    {items.length === 0 ? <p className="text-sm text-muted-foreground">Nenhum envio ainda.</p> : (
      <ul data-testid="broadcast-history" className="divide-y divide-zap-line rounded-md border border-zap-line">
        {items.map((b) => (
          <li key={b.id} data-testid={`broadcast-${b.id}`} className="space-y-0.5 px-3 py-2 text-sm">
            <p className="text-xs text-muted-foreground">{fmtDateTime(b.sent_at)} · por {b.sent_by}</p>
            <p className="line-clamp-2">{[b.text, b.audio_title && `Áudio: ${b.audio_title}`, b.video_title && `Vídeo: ${b.video_title}`, b.link_url && `Link: ${b.link_url}`].filter(Boolean).join(" + ")}</p>
            <p className="font-medium text-zap-deep">{b.recipients} receberam · {b.seen} já viram</p>
          </li>
        ))}
      </ul>
    )}
  </section>
);

export const SendPanel = ({ group, onSent }) => {
  const { api } = useOutletContext();
  const [text, setText] = useState("");
  const [audio, setAudio] = useState("");
  const [video, setVideo] = useState("");
  const [yt, setYt] = useState("");
  const [link, setLink] = useState("");
  const [lib, setLib] = useState({ replies: [], audios: [], videos: [], links: [] });
  const [scheduled, setScheduled] = useState([]);
  const [history, setHistory] = useState([]);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const loadHistory = () => {
    api.get("/admin/broadcasts", { params: { group_id: group.id } }).then((r) => setHistory(r.data)).catch(() => {});
    api.get("/admin/scheduled", { params: { group_id: group.id } }).then((r) => setScheduled(r.data)).catch(() => {});
  };
  const cancel = async (s) => {
    if (!window.confirm("Cancelar este envio agendado?")) return;
    try { await api.delete(`/admin/scheduled/${s.id}`); toast.success("Agendamento cancelado."); loadHistory(); } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => {
    Promise.all([api.get("/admin/quick-replies"), api.get("/admin/audios"), api.get("/admin/videos"), api.get("/admin/links")])
      .then(([r, a, v, l]) => setLib({ replies: r.data.filter((x) => x.active !== false), audios: a.data, videos: v.data.filter((x) => x.status === "pronto"), links: l.data })).catch(() => {});
  }, [api]);
  useEffect(() => { setText(""); setAudio(""); setVideo(""); setYt(""); setLink(""); loadHistory(); }, [group.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/groups/${group.id}/send`, { text, audio_id: audio || null, video_id: video || null, youtube_url: yt || null, link_url: link || null });
      toast.success(`Enviado para ${data.recipients} ${data.recipients === 1 ? "pessoa" : "pessoas"}.`);
      setText(""); setAudio(""); setVideo(""); setYt(""); setLink(""); loadHistory(); onSent();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false); setConfirm(false);
  };
  return (
    <div className="space-y-6">
      <section className="space-y-3 rounded-md border border-zap-line p-4">
        <h3 className="font-display text-base font-semibold">Enviar mensagem para o grupo</h3>
        <select data-testid="broadcast-template" value="" onChange={(e) => { const r = lib.replies.find((x) => x.id === e.target.value); if (r) setText(r.text); }} className={sel}>
          <option value="">Usar uma resposta pronta (opcional)</option>
          {lib.replies.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
        </select>
        <Textarea data-testid="broadcast-text" value={text} onChange={(e) => setText(e.target.value)} rows={5} maxLength={4000}
          placeholder="Olá, {primeiro_nome}! …" className="text-[15px]" />
        <p className="text-xs text-muted-foreground">{"{primeiro_nome}"} vira o primeiro nome de cada pessoa.</p>
        <select data-testid="broadcast-audio" value={audio} onChange={(e) => setAudio(e.target.value)} className={sel}>
          <option value="">Sem áudio</option>
          {lib.audios.map((a) => <option key={a.id} value={a.id}>Áudio: {a.title}</option>)}
        </select>
        <select data-testid="broadcast-video" value={video} onChange={(e) => setVideo(e.target.value)} className={sel}>
          <option value="">Sem vídeo da biblioteca</option>
          {lib.videos.map((v) => <option key={v.id} value={v.id}>Vídeo: {v.title}</option>)}
        </select>
        <input data-testid="broadcast-youtube" value={yt} onChange={(e) => setYt(e.target.value)} placeholder="Ou cole um link do YouTube (opcional)" className={sel} />
        {lib.links.length > 0 && (
          <select data-testid="broadcast-saved-link" value="" onChange={(e) => { const l = lib.links.find((x) => x.id === e.target.value); if (l) setLink(l.url); }} className={sel}>
            <option value="">Escolher um link pronto (opcional)</option>
            {lib.links.map((l) => <option key={l.id} value={l.id}>{l.title}</option>)}
          </select>
        )}
        <input data-testid="broadcast-link" value={link} onChange={(e) => setLink(e.target.value)} placeholder="Link para abrir: site, formulário, inscrição… (opcional)" className={sel} />
        <p className="text-xs text-muted-foreground">Tudo vai junto, nesta ordem: áudio, vídeo, link e a mensagem escrita.</p>
        <Button data-testid="broadcast-send" disabled={busy || group.count === 0 || (!text.trim() && !audio && !video && !yt.trim() && !link.trim())} onClick={() => setConfirm(true)} className="h-11 w-full gap-2">
          <Send className="h-4 w-4" />Enviar para {group.count} {group.count === 1 ? "pessoa" : "pessoas"}
        </Button>
      </section>
      <SchedulePicker group={group} ready={group.count > 0 && !!(text.trim() || audio || video || yt.trim() || link.trim())}
        content={{ text, audio_id: audio || null, video_id: video || null, youtube_url: yt || null, link_url: link || null }}
        onDone={() => { setText(""); setAudio(""); setVideo(""); setYt(""); setLink(""); loadHistory(); }} />
      <ScheduledList items={scheduled} onCancel={cancel} />
      <History items={history} />
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="broadcast-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Enviar para {group.count} {group.count === 1 ? "pessoa" : "pessoas"}?</AlertDialogTitle>
            <AlertDialogDescription>Cada pessoa do grupo “{group.name}” recebe a mensagem na própria conversa do ZAP IUDP. Quem ativou os avisos recebe uma notificação no celular.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="broadcast-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="broadcast-confirm-send" onClick={(e) => { e.preventDefault(); send(); }} disabled={busy}>{busy ? "Enviando…" : "Enviar agora"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
